import React from 'react';
import { Col, Row, Typography, theme } from 'antd';
import { buildStatViewModel, STAT_DISPLAY_BREAKDOWN, STAT_DISPLAY_TOTAL, STAT_MAX_BREAKDOWN } from './reportingStatCard';

const { Text } = Typography;

function formatCount(n) {
    return Number.isFinite(n) ? n.toLocaleString() : '—';
}

function StatCountTile({
    label,
    value,
    token,
    expanded = false,
    prominent = false,
}) {
    const valueFont = prominent ? (expanded ? 40 : 28) : (expanded ? 24 : 20);
    const labelFont = prominent ? (expanded ? 13 : 11) : (expanded ? 12 : 10);
    const padding = prominent
        ? (expanded ? '20px 16px' : '14px 12px')
        : (expanded ? '14px 10px' : '10px 8px');

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                padding,
                background: token.colorFillAlter,
                border: `1px solid ${token.colorBorderSecondary}`,
                borderRadius: token.borderRadiusLG,
                boxSizing: 'border-box',
                height: '100%',
                width: '100%',
                ...(prominent
                    ? { flex: 1, minHeight: 0 }
                    : { minHeight: expanded ? 64 : 52 }),
            }}
        >
            <Text
                ellipsis={{ tooltip: label }}
                style={{
                    fontSize: labelFont,
                    fontWeight: 600,
                    color: token.colorTextSecondary,
                    textTransform: prominent ? 'none' : 'uppercase',
                    letterSpacing: prominent ? 0 : '0.04em',
                    display: 'block',
                    marginBottom: prominent ? 8 : 6,
                }}
            >
                {label || '—'}
            </Text>
            <Text
                style={{
                    fontSize: valueFont,
                    fontWeight: 600,
                    lineHeight: 1.15,
                    color: token.colorText,
                    fontVariantNumeric: 'tabular-nums',
                }}
            >
                {formatCount(value)}
            </Text>
        </div>
    );
}

/**
 * Count / KPI tiles with bordered cards (matches print stat-card styling).
 */
export default function StatCardView({ dataset, vizSpec, expanded = false }) {
    const { token } = theme.useToken();
    const model = buildStatViewModel(dataset, vizSpec);
    if (!model) return null;

    const { display, items, total, valueLabel } = model;

    if (display === STAT_DISPLAY_TOTAL) {
        const primary = items.length === 1 ? items[0] : null;
        const title = primary?.label && primary.label !== valueLabel ? primary.label : valueLabel;
        const value = primary && items.length === 1 ? primary.value : total;

        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    minHeight: expanded ? 160 : 88,
                    padding: expanded ? 20 : 10,
                    boxSizing: 'border-box',
                }}
            >
                <StatCountTile
                    label={title}
                    value={value}
                    token={token}
                    expanded={expanded}
                    prominent
                />
                {items.length > 1 && (
                    <Text style={{
                        display: 'block',
                        marginTop: 10,
                        fontSize: 11,
                        textAlign: 'center',
                        color: token.colorTextTertiary,
                    }}
                    >
                        {items.length} categories
                    </Text>
                )}
            </div>
        );
    }

    const shown = items.slice(0, STAT_MAX_BREAKDOWN);
    const colSpan = shown.length <= 2 ? 12 : shown.length <= 4 ? 12 : 8;

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'auto',
                padding: expanded ? 20 : 10,
                boxSizing: 'border-box',
            }}
        >
            <Row gutter={[12, 12]} align="stretch" style={{ flex: 1 }}>
                {shown.map(item => (
                    <Col
                        key={item.label || item.value}
                        xs={colSpan}
                        sm={colSpan}
                        md={colSpan >= 12 ? 12 : 6}
                        style={{ display: 'flex' }}
                    >
                        <StatCountTile
                            label={item.label}
                            value={item.value}
                            token={token}
                            expanded={expanded}
                        />
                    </Col>
                ))}
            </Row>
            {items.length > STAT_MAX_BREAKDOWN && (
                <Text style={{
                    display: 'block',
                    marginTop: 10,
                    fontSize: 11,
                    textAlign: 'center',
                    color: token.colorTextTertiary,
                }}
                >
                    +{items.length - STAT_MAX_BREAKDOWN} more (total {formatCount(total)})
                </Text>
            )}
        </div>
    );
}

export { STAT_DISPLAY_BREAKDOWN, STAT_DISPLAY_TOTAL };
